import moment from 'moment';
import EastIcon from '@mui/icons-material/East';
import { Button, Stack, SvgIcon, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  billingExportDownloadTreatment,
  enterpriseRbacEnforcementTreatment,
  prelimInvoiceReviewTreatment,
} from 'src/config';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { usePermission } from 'src/hooks/use-permission';
import { paths } from 'src/paths';
import { IBillingBlocker } from 'src/services/billingExportService/types';
import { BillingBlockersBanner, BillingBlockersDialog } from './BillingBlockers';
import BillingExportButton from './BillingExportButton';
import { isHardClosedPeriod } from './closedPeriod';
import NoPreliminaryReviewPanel from './NoPreliminaryReviewPanel';
import { rememberPeriod } from '../preliminary-invoice-review/periodHandoff';

type Props = {
  //  `location.state` from the alert view — the period's date range plus the closeout status and
  //  division forwarded from the Closeout Alerts row.
  dateRange: any;
  /**
   * Finance has closed the books on the financial period this one falls in (BOPS-170). Read by the
   * page rather than here so the status pill above and this bar can never disagree about it.
   */
  isLocked?: boolean;
  /** False while that read is still in flight. */
  isLockKnown?: boolean;
  /**
   * BOPS-440 — told whenever the billing export becomes blocked or stops being blocked, so the
   * page can drop its "You've completed all your alerts!" card while the download is stopped.
   */
  onExportBlockedChange?: (isBlocked: boolean) => void;
};

/**
 * Actions unlocked by a hard close, shown above the period's alert interface.
 * BOPS-116 adds "Download billing export" here alongside, behind its own toggle. BOPS-294 also
 * gates it on the DOWNLOAD_BILLING_EXPORT grant: the export is the facility's full revenue picture.
 *
 * BOPS-170 splits what follows a re-close in two, on whether finance has locked the financial
 * period. While it is open (Path A) this is unchanged. Once it is locked (Path B) there is no
 * invoice left to review, so the preliminary review is *removed* and the panel below explains why.
 * The billing export is untouched either way — the export is still the facility's to pull.
 */
const ClosedPeriodActionBar = (props: Props) => {
  const { dateRange, isLocked = false, isLockKnown = true, onExportBlockedChange } = props;
  const navigate = useNavigate();
  const [blockers, setBlockers] = useState<IBillingBlocker[]>([]);
  const [isBlockersOpen, setIsBlockersOpen] = useState(false);
  const [isPrelimInvoiceReviewEnabled] = useFeatureToggleWhenReady(
    prelimInvoiceReviewTreatment,
    'ENTERPRISE'
  );
  const [isBillingExportEnabled] = useFeatureToggleWhenReady(
    billingExportDownloadTreatment,
    'ENTERPRISE'
  );
  const canDownloadBillingExport = usePermission('DOWNLOAD_BILLING_EXPORT');
  //  Enforcement follows the enterprise RBAC treatment; off or unresolved is fail-open.
  const [isRbacEnforced] = useFeatureToggleWhenReady(
    enterpriseRbacEnforcementTreatment,
    'ENTERPRISE'
  );

  //  The hooks return null until Split is ready — hold the actions back rather than
  //  flashing them on and off. Each action owns its own treatment, so the bar only
  //  disappears once neither is enabled.
  const showPrelimInvoiceReview = isPrelimInvoiceReviewEnabled === true;
  const showBillingExport =
    isBillingExportEnabled === true && (isRbacEnforced !== true || canDownloadBillingExport);

  //  The button and the panel are two faces of the same feature, so they share its toggle: with
  //  the preliminary review switched off there is no review to explain the absence of. Both wait
  //  on the lock for the same reason the Split hooks are awaited above — offering the review and
  //  then taking it away mid-render is the one impression this story exists to prevent.
  const showPrelimButton = showPrelimInvoiceReview && isLockKnown && !isLocked;
  const showLockedPanel = showPrelimInvoiceReview && isLockKnown && isLocked;
  const showStrip = showBillingExport || showPrelimButton;

  //  BOPS-440 — the banner and its list belong to the download, so they share its gates.
  const isExportBlocked = isHardClosedPeriod(dateRange) && showBillingExport && blockers.length > 0;

  const handleBlockersChange = useCallback(
    (next: IBillingBlocker[], options?: { openList: boolean }) => {
      setBlockers(next);
      if (!next.length) setIsBlockersOpen(false);
      else if (options?.openList) setIsBlockersOpen(true);
    },
    []
  );

  useEffect(() => {
    onExportBlockedChange?.(isExportBlocked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExportBlocked]);

  if (!isHardClosedPeriod(dateRange)) return null;
  //  Guards the strip and the panel separately: on Path B with the export switched off the strip
  //  is empty, and an empty grey bar above the panel is worse than no bar.
  if (!showStrip && !showLockedPanel) return null;

  return (
    <Stack
      spacing={1.5}
      sx={{ marginTop: '1rem' }}
    >
      {isExportBlocked && (
        <BillingBlockersBanner
          count={blockers.length}
          onView={() => setIsBlockersOpen(true)}
        />
      )}

      {showStrip && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1.5}
          sx={{
            backgroundColor: '#F8F9FC',
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            padding: '12px 16px',
            '& > [data-slot="refresh-status"]': { marginLeft: { sm: 'auto' } },
          }}
        >
          {showBillingExport && (
            <BillingExportButton
              dateRange={dateRange}
              onBlockersChange={handleBlockersChange}
            />
          )}

          {showPrelimButton && (
            <Button
              variant="outlined"
              sx={{ backgroundColor: '#FFFFFF' }}
              endIcon={
                <SvgIcon fontSize="small">
                  <EastIcon />
                </SvgIcon>
              }
              /*  The period now travels in the URL rather than location.state: the review is the
                  rebill plugin, and a state-only route cannot be linked to or reloaded.

                  The row is parked on the way out purely so "Back to the period" can rebuild this
                  page, which reads its whole period from location.state — the plugin's back button
                  leaves via a relative `..`, and that drops router state (BOPS-131). */
              onClick={() => {
                rememberPeriod(dateRange);
                //  The closeout's OWN dates travel alongside the month. A month can be closed in
                //  several shorter ranges, and the month alone made the review widen every one of
                //  them to the full month - which is its request range, not just its label.
                const start = moment(dateRange?.startDate).format('YYYY-MM-DD');
                const end = moment(dateRange?.endDate).format('YYYY-MM-DD');
                const range =
                  dateRange?.startDate && dateRange?.endDate ? `?start=${start}&end=${end}` : '';
                navigate(
                  `/${paths.preliminaryInvoiceReview}/${moment(dateRange?.startDate).format(
                    'YYYY-MM'
                  )}${range}`
                );
              }}
            >
              <Typography variant="buttonText">Open preliminary invoice review</Typography>
            </Button>
          )}
        </Stack>
      )}

      {showLockedPanel && <NoPreliminaryReviewPanel dateRange={dateRange} />}

      {isExportBlocked && (
        <BillingBlockersDialog
          open={isBlockersOpen}
          onClose={() => setIsBlockersOpen(false)}
          blockers={blockers}
          startDate={dateRange?.startDate}
          endDate={dateRange?.endDate}
        />
      )}
    </Stack>
  );
};

export default ClosedPeriodActionBar;
